import {
  computed,
  ref,
  toValue,
  type MaybeRefOrGetter,
} from 'vue'

import { useLegacyAutocomplete } from '@/composables/current-student-advanced-standing-form/useLegacyAutocomplete'
import { displaySubject } from '@/models/current-student-advanced-standing-form/display'
import {
  externalCourseRequestTarget,
  externalSubjectRequestTarget,
  findExternalSubjectForRequest,
  setSubjectRequestTargetCode,
  workExperienceSubjectRequestTarget,
  type PendingSubjectRequestTarget,
} from '@/models/current-student-advanced-standing-form/subjectRequests'
import {
  clearModalAlert,
  type AlertTone,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  ExternalCourse,
  ExternalSubject,
  SubjectSearchItem,
  SubjectSearchQuery,
  SubjectSearchResponse,
  WorkExperience,
} from '@/models/current-student-advanced-standing-form/api'

type UseSubjectRequestModalControllerOptions = {
  externalCourses: MaybeRefOrGetter<ExternalCourse[]>
  electiveList: MaybeRefOrGetter<SubjectSearchItem[]>
  searchSubjects: (query: SubjectSearchQuery) => Promise<SubjectSearchResponse>
}

export function useSubjectRequestModalController({
  externalCourses,
  electiveList,
  searchSubjects,
}: UseSubjectRequestModalControllerOptions) {
  const ltuSubjectModalVisible = ref(false)
  const ltuSubjectSaving = ref(false)
  const ltuSubjectModalError = ref('')
  const ltuSubjectModalAlertTone = ref<AlertTone>('danger')
  const currentSubjectRequestTarget = ref<PendingSubjectRequestTarget | null>(null)
  const ltuSubjectSearchText = ref('')
  const selectedLtuSubjectDisplay = ref('')
  const ltuSubjectElectiveCode = ref('')
  const ltuSubjectSearchTimer = ref<number | undefined>()
  const subjectSelectedConfirmModalVisible = ref(false)
  const unspecifiedSubjectsWarningEnable = ref(true)

  const {
    suggestions: ltuSubjectSuggestions,
    activeIndex: ltuSubjectActiveIndex,
    setSuggestions: setLtuSubjectSuggestions,
    clearSuggestions: clearLtuSubjectSuggestions,
    resetActiveIndex: resetLtuSubjectActiveIndex,
    handleKeydown: handleLtuSubjectAutocompleteKeydown,
  } = useLegacyAutocomplete<SubjectSearchItem>({
    selectItem: selectLtuSubject,
  })

  const currentExternalSubjectForRequest = computed(() => {
    return findExternalSubjectForRequest(currentSubjectRequestTarget.value, toValue(externalCourses))
  })

  const ltuSubjectElectiveOptions = computed(() => {
    const externalSubject = currentExternalSubjectForRequest.value
    if (externalSubject == null) return toValue(electiveList)

    return toValue(electiveList).filter((subject) => {
      return subject.subject_year_level === Number(externalSubject.external_subject_level)
    })
  })

  function openSubjectRequestModal(externalSubject: ExternalSubject): void {
    currentSubjectRequestTarget.value = externalSubjectRequestTarget(externalSubject)
    openLtuSubjectModal()
  }

  function openCourseSubjectRequestModal(externalCourse: ExternalCourse): void {
    currentSubjectRequestTarget.value = externalCourseRequestTarget(externalCourse)
    openLtuSubjectModal()
  }

  function openWorkExpSubjectRequestModal(workExperience: WorkExperience): void {
    currentSubjectRequestTarget.value = workExperienceSubjectRequestTarget(workExperience)
    openLtuSubjectModal()
  }

  function openLtuSubjectModal(): void {
    ltuSubjectSearchText.value = ''
    selectedLtuSubjectDisplay.value = ''
    ltuSubjectElectiveCode.value = ''
    clearLtuSubjectSuggestions()
    clearModalAlert(ltuSubjectModalError, ltuSubjectModalAlertTone)
    ltuSubjectModalVisible.value = true
  }

  function closeLtuSubjectModal(): void {
    ltuSubjectModalVisible.value = false
  }

  async function onLtuSubjectSearchInput(): Promise<void> {
    resetLtuSubjectActiveIndex()
    ltuSubjectElectiveCode.value = ''

    if (ltuSubjectSearchText.value.length < 2) {
      clearLtuSubjectSuggestions()
      return
    }

    if (ltuSubjectSearchTimer.value != null) {
      window.clearTimeout(ltuSubjectSearchTimer.value)
    }

    ltuSubjectSearchTimer.value = window.setTimeout(() => {
      void loadLtuSubjectSuggestions(ltuSubjectSearchText.value)
    }, 300)
  }

  async function loadLtuSubjectSuggestions(search: string): Promise<void> {
    try {
      const result = await searchSubjects({
        search,
        basic_as: true,
      })
      setLtuSubjectSuggestions(result.data.map((subject) => ({
        ...subject,
        value: displaySubject(subject),
      })))
    } catch {
      clearLtuSubjectSuggestions()
    }
  }

  function selectLtuSubject(subject: SubjectSearchItem): void {
    const subjectDisplay = displaySubject(subject)
    currentSubjectRequestTarget.value = setSubjectRequestTargetCode(currentSubjectRequestTarget.value, subject.subject_code)
    ltuSubjectSearchText.value = subjectDisplay
    selectedLtuSubjectDisplay.value = subjectDisplay
    ltuSubjectElectiveCode.value = ''
    clearLtuSubjectSuggestions()
  }

  function blurLtuSubjectSearch(): void {
    window.setTimeout(() => {
      if (currentSubjectRequestTarget.value?.subject_code == null) {
        ltuSubjectSearchText.value = ''
        selectedLtuSubjectDisplay.value = ''
      } else {
        ltuSubjectSearchText.value = selectedLtuSubjectDisplay.value
      }
      clearLtuSubjectSuggestions()
    }, 10)
  }

  function onLtuSubjectSearchKeydown(event: KeyboardEvent): void {
    handleLtuSubjectAutocompleteKeydown(event)
  }

  function onLtuSubjectElectiveChange(): void {
    currentSubjectRequestTarget.value = setSubjectRequestTargetCode(currentSubjectRequestTarget.value, null)
    ltuSubjectSearchText.value = ''
    selectedLtuSubjectDisplay.value = ''
    clearLtuSubjectSuggestions()
  }

  return {
    ltuSubjectModalVisible,
    ltuSubjectSaving,
    ltuSubjectModalError,
    ltuSubjectModalAlertTone,
    currentSubjectRequestTarget,
    ltuSubjectSearchText,
    selectedLtuSubjectDisplay,
    ltuSubjectElectiveCode,
    ltuSubjectSearchTimer,
    subjectSelectedConfirmModalVisible,
    unspecifiedSubjectsWarningEnable,
    ltuSubjectSuggestions,
    ltuSubjectActiveIndex,
    currentExternalSubjectForRequest,
    ltuSubjectElectiveOptions,
    openSubjectRequestModal,
    openCourseSubjectRequestModal,
    openWorkExpSubjectRequestModal,
    openLtuSubjectModal,
    closeLtuSubjectModal,
    onLtuSubjectSearchInput,
    loadLtuSubjectSuggestions,
    selectLtuSubject,
    blurLtuSubjectSearch,
    onLtuSubjectSearchKeydown,
    onLtuSubjectElectiveChange,
  }
}
